import { BehaviorSubject, Observable, of } from 'rxjs';
import { Component, OnInit, inject } from '@angular/core';
import { catchError, map, take } from 'rxjs/operators';
import { MatDialogRef } from '@angular/material/dialog';
import { UserService } from './user.service';
import { select, Store } from '@ngrx/store';
import { userDataView } from '../../model/views';
import { UserData } from '../../model/user/user.model';
import { AppState } from '../../model/reducer';
import { LoginPasswordExpired } from '../../model/user/user.actions';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'adm4-session-expiry-dialog',
    template: `
    <adm4-modal-dialog-title header='Session expiry'>
      <div class='login-container session-dialog-container'>
        <h1>Session has expired</h1>
        <div class='login-text'>Log back in to nevisAdmin 4</div>
    
        <form name='form'
          #f='ngForm'
          novalidate>
          <div class='adm4-form-group'>
            <div class="input-group position-relative">
              <input type='text'
                class='form-control login-control-border-radius'
                name='username'
                disabled
                [ngModel]='userName$ | async'
                required/>
              <!-- Edge didn't display svg in background when it's cached, therefore this workaround is applied to avoid that problem, because no idea why it happens, rly -->
              <img src='assets/login-profil-off.svg' alt='' class='input-group-text input-group-icon username-addon-off'/>
              <img src='assets/login-profil-on.svg' alt='' class='input-group-text input-group-icon username-addon'/>
            </div>
          </div>
          <div class='adm4-form-group' [ngClass]="{ 'has-error': f.submitted && !password.valid }">
            <div class="input-group position-relative">
              <input type='password'
                class='form-control login-control-border-radius'
                name='password'
                placeholder='Password'
                [(ngModel)]='passwordModel'
                (ngModelChange)='resetError()'
                #password='ngModel'
                cdkFocusInitial
                required/>
              <span class='input-group-text input-group-icon password-addon'></span>
            </div>
            <div class='validation-message-container'>
              @if (f.submitted && !password.valid) {
                <div class='invalid-feedback'>
                  <adm4-validation-indicator [isDisplayed]='true' [isError]='true' [diameter]='8'></adm4-validation-indicator>
                  Password is required
                </div>
              }
              @if (passwordHasError$ | async) {
                <div class='error-message'>
                  <adm4-validation-indicator [isDisplayed]='true' [isError]='true' [diameter]='8'></adm4-validation-indicator>
                  {{INVALID_LOGIN}}
                </div>
              }
            </div>
          </div>
          <a (click)='closeDialog()'>Log in with another user</a>
          <div class='adm4-form-group'>
            @if ((userName$ | async); as userName) {
              <button (click)='login(userName)' class="login-control-border-radius">Login</button>
            }
          </div>
        </form>
      </div>
    </adm4-modal-dialog-title>
    `,
    styleUrls: ['login.component.scss', 'session-expiry-dialog.component.scss'],
    standalone: false
})
export class SessionExpiryDialogComponent implements OnInit {
  private readonly dialogRef = inject<MatDialogRef<SessionExpiryDialogComponent, boolean>>(MatDialogRef);
  private readonly userService = inject(UserService);
  private readonly store$ = inject<Store<AppState>>(Store);

  userName$: Observable<string>;
  passwordModel: string;

  private readonly _passwordError$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  passwordHasError$: Observable<boolean> = this._passwordError$.asObservable();

  private _passwordExpiredHandled = false;

  readonly INVALID_LOGIN = 'Password is incorrect, please try again';

  ngOnInit(): void {
    this.userName$ = this.store$.pipe(
      select(userDataView),
      map((user: UserData) => user.userKey)
    );
  }

  login(prefilledUsername: string): void {
    this.userService.login(prefilledUsername, this.passwordModel).pipe(
      map(() => {
        this._passwordError$.next(false);
        return true;
      }), catchError((error: HttpErrorResponse) => {
        if (error.status === 403 && error.error?.error === 'PASSWORD_EXPIRED') {
          this._passwordError$.next(false);
          if (!this._passwordExpiredHandled) {
            this._passwordExpiredHandled = true;
            this.dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
              this.store$.dispatch(new LoginPasswordExpired(prefilledUsername));
            });
            this.dialogRef.close(true);
          }
          return of(false);
        }
        this._passwordError$.next(true);
        return of(false);
      })).subscribe((loginSuccess: boolean) => {
      if (loginSuccess) {
        this.dialogRef.close(true);
      }
    });
  }

  resetError() {
    this._passwordError$.next(false);
  }

  closeDialog(): void {
    this.dialogRef.close(false);
  }
}
